import angular from 'angular';
import d3 from 'd3';
import { startCase, forEach, map } from 'lodash';

const margin = {
  top: 0.5,
  right: 50.5,
  bottom: 25.5,
  left: 0.5,
};

function extent(data, fn) {
  return [
    d3.min(data, series => d3.min(series, fn)),
    d3.max(data, series => d3.max(series, fn)),
  ];
}

module.exports = angular.module('common.components.od-market-analysis-chart', [
])
  .component('odMarketAnalysisChart', {
      bindings: {
        statistic: '@',
        postalCode: '@',
        market: '@',
        marketLabel: '@',
        smoothing: '@',
        format: '&', // callback function
      },
      controller: class {
        constructor($scope, $element, $window, $q) {
          this.series = [];
          const windowEl = angular.element($window);
        $q.all(map(this.series, '$promise'))
           .then(this.smooth.bind(this))
           .then(() => {
             this.svg = d3.select($element[0]).append('svg');
             this.x = d3.time.scale()
               .domain(extent(this.series, d => d[0]));
             this.y = d3.scale.linear()
               .domain([0, extent(this.series, d => d[1])[1]])
               .nice();
             this.line = d3.svg.line().interpolate('linear')
               .defined(d => d[1])
               .interpolate('cardinal')
               .tension(0.8)
               .x(d => this.x(d[0]))
               .y(d => this.y(d[1]));

             var onWindowResize = () => {
               this.resize($element.prop('offsetWidth'));
             };
             windowEl.on('resize', onWindowResize);
             $scope.$on('$destroy', () => {
               windowEl.off('resize', onWindowResize);
             });

             onWindowResize();
           });
       }

       resize(width) {
         if (this.width === width)
           return;

         this.width = width;
         this.height = Math.round(this.width * 0.4);
         this.draw();
       }

       smooth(series) {
         if (!this.smoothing) return;

         // https://en.wikipedia.org/wiki/Exponential_smoothing#The_exponential_moving_average
         let a = Number(this.smoothing);
         forEach(series, (series) => {
           forEach(series, (d, i, data) => {
             if (i > 0 && d[1] !== null && data[i-1][1] !== null) {
               d[1] = a * d[1] + (1 - a) * data[i-1][1];
             }
           });
         });
       }

       draw() {
         this.svg
           .attr('width', this.width)
           .attr('height', this.height);
         this.chart = this.chart || this.svg.append('g')
           .attr('class', 'chart')
           .attr('transform', `translate(${margin.left}, ${margin.top})`);

         var width = this.width - margin.left - margin.right;
         var height = this.height - margin.top - margin.bottom;

         // y-axis
         this.y
           .range([height, 0]);
         var yAxis = d3.svg.axis()
           .scale(this.y)
           .orient('right')
           .ticks(4)
           .tickFormat(d => this.format({ value: d }) || d);
         this.yAxisGroup = this.yAxisGroup || this.chart.append('g')
           .attr('class', 'y axis');
         this.yAxisGroup
           .attr('transform', `translate(${width}, 0)`)
           .call(yAxis);
         // y grid lines
         var yGrid = yAxis.tickSize(width, 0, 0).tickFormat('');
         this.yGridGroup = this.yGridGroup || this.chart.append('g')
           .attr('class', 'y grid');
         this.yGridGroup
           .call(yGrid);

         // x-axis
         this.x
           .range([0, width]);
         var xAxis = d3.svg.axis()
           .scale(this.x)
           .orient('bottom')
           .ticks(5)
           .tickFormat(d3.time.format('%Y'));
         this.xAxisGroup = this.xAxisGroup || this.chart.append('g')
           .attr('class', 'x axis');
         this.xAxisGroup
           .attr('transform', `translate(0, ${height})`)
           .call(xAxis);

         // legend
         var legendWidth = 80;
         var legendTranslation = margin.left + width - legendWidth * this.series.length;
         this.legend = this.legend || this.svg.append('g')
           .attr('class', 'legend');
         this.legend
           .attr('transform', `translate(${legendTranslation}, -12)`);
         var legends = this.legend.selectAll('g')
           .data(this.series)
           .enter()
             .append('g')
               .attr('transform', (area, i) => `translate(${i*legendWidth}, 0)`);
         legends
           .append('line')
             .attr('class', d => d.entity_type)
             .attr('x2', 20)
             .attr('y2', 0);
         legends
           .append('text')
           .attr('x', 23)
           .attr('dy', '.32em')
           .text(d => {
             if (d.entity_type === 'market') {
               return this.marketLabel;
             } else {
               return startCase(d.entity_id);
             }
           });

         // lines
         var linePaths = this.chart.selectAll('path.line')
           .data(this.series);
         linePaths.enter()
           .append('path')
           .attr('class', d => `line ${d.entity_type}`);
         linePaths
           .attr('d', area => this.line(area));
         }
       }
})
.name;



/** WEBPACK FOOTER **
 ** ./javascripts/common/components/od-market-analysis-chart.js
 **/